// useIncrementalPagination.js

import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Nearest scrollable ancestor for IntersectionObserver root.
 * Nested panes (Source, Gallery) scroll inside a div, not the viewport.
 *
 * @param {Element|null} el
 * @returns {Element|null}
 */
function getScrollParent(el) {
  let node = el?.parentElement || null;
  while (node) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
      && node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Slice `items` for infinite-scroll style UI paging.
 *
 * `resetKey` must be a primitive (string/number). Do not pass a changing
 * `nodes` array reference — live Firestore updates would reset the window.
 *
 * @param {{
 *   items: unknown[],
 *   initialCount: number,
 *   step: number,
 *   resetKey?: string|number,
 *   threshold?: number,
 * }} options
 */
const useIncrementalPagination = ({
  items,
  initialCount,
  step,
  resetKey = '',
  threshold = 0,
}) => {
  const [displayCount, setDisplayCount] = useState(initialCount);
  const loadMoreRef = useRef(null);
  const itemCount = Array.isArray(items) ? items.length : 0;

  const displayedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    return items.slice(0, displayCount);
  }, [items, displayCount]);

  const hasMore = displayedItems.length < itemCount;

  useEffect(() => {
    setDisplayCount(initialCount);
  }, [initialCount, resetKey]);

  // If the list shrinks (filter/search), clamp; never reset upward progress.
  useEffect(() => {
    setDisplayCount((prev) => {
      if (itemCount <= 0) return initialCount;
      if (prev > itemCount) return itemCount;
      return prev;
    });
  }, [itemCount, initialCount]);

  useEffect(() => {
    if (!hasMore) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const el = loadMoreRef.current;
    if (!el) return undefined;

    const root = getScrollParent(el);
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry?.isIntersecting) return;
      setDisplayCount((prev) => Math.min(prev + step, itemCount));
    }, {
      root,
      rootMargin: '120px 0px',
      threshold,
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, itemCount, step, threshold, displayCount]);

  return {
    displayedItems,
    hasMore,
    loadMoreRef,
  };
};

export default useIncrementalPagination;
